"use client";

import {
  type FloatingChatPresentation,
  type FloatingChatSide,
  isFloatingChatSide,
} from "./floating-chat-context";

/**
 * A snapshot of the shell-owned state a host may want to persist: whether the
 * panel is open, which layout it is in, and — on a `draggable` root — which
 * corner the user moved the launcher to. The active chat id is deliberately
 * NOT part of this snapshot — chat selection is consumer-owned (the shell
 * never creates or selects chats), so hosts persist it alongside this seam;
 * {@link FloatingUrlPersistence} ships `readChatId`/`writeChatId` helpers for
 * the common URL-param case.
 */
export interface FloatingChatStateSnapshot {
  open: boolean;
  /**
   * Omitted from `write()` when the shell's presentation is controlled
   * (a locked `mode`): the user made no layout choice, so implementations
   * must leave any stored layout preference untouched and fall back to it
   * (or their default) where a concrete layout is needed.
   */
  presentation?: FloatingChatPresentation;
  /**
   * Present only on the write a corner move makes: implementations remember
   * it, and leave any stored corner untouched when it is omitted. Restored
   * from `read()` only on a `draggable` root.
   */
  side?: FloatingChatSide;
}

/**
 * The persistence seam for `<FloatingChat.Root statePersistence={...}>`.
 * Implement `read` (applied once on mount — return only the fields you want
 * to restore) and `write` (called from the shell's event handlers whenever
 * open/presentation/corner changes). Router-bound hosts (Next, Remix, ...) implement
 * this over their router; anything else can use
 * {@link createFloatingUrlPersistence}.
 */
export interface FloatingChatStatePersistence {
  read(): Partial<FloatingChatStateSnapshot>;
  write(state: FloatingChatStateSnapshot): void;
}

export interface FloatingUrlPersistenceOptions {
  /**
   * URL param carrying the active chat id (`?mchat=<id>`), managed via
   * `readChatId`/`writeChatId`. Pass `null` to disable. Default `"mchat"`.
   */
  chatParam?: string | null;
  /**
   * localStorage key remembering the last docked/floating layout across
   * sessions. Pass `null` to disable. Default `"magnus:presentation"`.
   */
  layoutStorageKey?: string | null;
  /**
   * URL param reflecting open + layout: `?magnus=floating|sidebar` when open,
   * absent when closed. Fullscreen is intentionally URL-transient (reload
   * falls back to the remembered layout). Default `"magnus"`.
   */
  param?: string;
  /**
   * localStorage key remembering the corner a `draggable` launcher was moved
   * to. Pass `null` to disable. Default `"magnus:floating-chat-side"`.
   */
  sideStorageKey?: string | null;
}

export interface FloatingUrlPersistence extends FloatingChatStatePersistence {
  /** Current active chat id from the URL (`?mchat=` by default). */
  readChatId(): string | null;
  /** Persist (or clear, with `null`) the active chat id in the URL. */
  writeChatId(chatId: string | null): void;
}

const DEFAULT_PARAM = "magnus";
const DEFAULT_CHAT_PARAM = "mchat";
const DEFAULT_LAYOUT_KEY = "magnus:presentation";
const DEFAULT_SIDE_KEY = "magnus:floating-chat-side";

/** Layouts that round-trip through the URL + storage. Fullscreen does not. */
function isPersistablePresentation(
  value: string | null
): value is FloatingChatPresentation {
  return value === "floating" || value === "sidebar";
}

/**
 * Router-free persistence for the floating shell: open + layout live in a URL
 * param (shareable/reload-safe), the last used layout is remembered in
 * `localStorage`, and `history.replaceState` keeps the change out of the
 * browser history stack. Works in any SPA (Vite, CRA, ...) with zero setup:
 *
 * ```tsx
 * const persistence = createFloatingUrlPersistence();
 * <FloatingChat.Root statePersistence={persistence} ...>
 * ```
 *
 * SSR-safe: every method no-ops without `window`.
 */
export function createFloatingUrlPersistence(
  options: FloatingUrlPersistenceOptions = {}
): FloatingUrlPersistence {
  const param = options.param ?? DEFAULT_PARAM;
  const chatParam =
    options.chatParam === undefined ? DEFAULT_CHAT_PARAM : options.chatParam;
  const layoutKey =
    options.layoutStorageKey === undefined
      ? DEFAULT_LAYOUT_KEY
      : options.layoutStorageKey;
  const sideKey =
    options.sideStorageKey === undefined
      ? DEFAULT_SIDE_KEY
      : options.sideStorageKey;

  const readParams = () =>
    typeof window === "undefined"
      ? null
      : new URLSearchParams(window.location.search);

  const replaceParams = (params: URLSearchParams) => {
    if (typeof window === "undefined") {
      return;
    }
    const query = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
    );
  };

  // `localStorage` is not merely absent under SSR — accessing it *throws* when
  // storage is blocked, which is routine for this widget's target: Safari with
  // cookies disabled, sandboxed iframes, hardened enterprise browsers. A
  // remembered layout or corner is a nicety, so both sides degrade to no-op.
  const readStored = (key: string | null): string | null => {
    if (!key || typeof window === "undefined") {
      return null;
    }
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const writeStored = (key: string | null, value: string) => {
    if (!key) {
      return;
    }
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* storage blocked — the value just won't survive the reload */
    }
  };

  const readLayoutPreference = (): FloatingChatPresentation | undefined => {
    const stored = readStored(layoutKey);
    return isPersistablePresentation(stored) ? stored : undefined;
  };

  const readSide = (): FloatingChatSide | undefined => {
    const stored = readStored(sideKey);
    return isFloatingChatSide(stored) ? stored : undefined;
  };

  return {
    read() {
      const side = readSide();
      const params = readParams();
      const fromUrl = params?.get(param) ?? null;
      if (isPersistablePresentation(fromUrl)) {
        return { open: true, presentation: fromUrl, side };
      }
      // Closed (or fullscreen, which does not round-trip): restore only the
      // remembered layout so the next open lands where the user left it.
      return { open: false, presentation: readLayoutPreference(), side };
    },
    write({ open, presentation, side }) {
      const params = readParams();
      if (!params) {
        return;
      }
      if (side !== undefined) {
        writeStored(sideKey, side);
      }
      if (
        presentation !== undefined &&
        isPersistablePresentation(presentation)
      ) {
        writeStored(layoutKey, presentation);
      }
      params.delete(param);
      if (open) {
        // No presentation (locked mode) → reflect the remembered layout in
        // the URL without touching the stored preference.
        params.set(param, presentation ?? readLayoutPreference() ?? "floating");
      }
      replaceParams(params);
    },
    readChatId() {
      if (!chatParam) {
        return null;
      }
      return readParams()?.get(chatParam) ?? null;
    },
    writeChatId(chatId) {
      if (!chatParam) {
        return;
      }
      const params = readParams();
      if (!params) {
        return;
      }
      if (chatId) {
        params.set(chatParam, chatId);
      } else {
        params.delete(chatParam);
      }
      replaceParams(params);
    },
  };
}
